'use client'

import React, { memo } from 'react'
import { IBanner } from '@/app/[locale]/_types'
import {
  AspectRatio,
  Box,
  Center,
  Text,
  useBreakpointValue,
} from '@chakra-ui/react'
import Image from 'next/image'
import Link from 'next/link'
import { useCustomLocale } from '@/app/[locale]/_hooks/useCustomLocale'
import Carousel from './Carousel'

const isExternalUrl = (url: string | undefined): boolean => {
  if (!url || typeof window === 'undefined') return false
  return !url.includes(window.location.hostname)
}

const getFullUrl = (
  url: string | undefined,
  baseUrl: string | undefined
): string => {
  if (!url || !baseUrl) return ''
  return url.includes(baseUrl) ? url : `${baseUrl}${url}`
}

const aspectRatio = 4 / 2

const Banner = ({ list }: { list: IBanner[] }) => {
  const currentLocale = useCustomLocale()
  const isDesktop = useBreakpointValue(
    { base: false, lg: true },
    { ssr: false }
  )

  const slides = React.useMemo(
    () =>
      list
        ?.filter((item) => item?.image?.[currentLocale])
        .map((item, index) => {
          const imageUrl = getFullUrl(
            item?.image?.[currentLocale],
            process.env.BASE_URL
          )

          const videoUrl =
            item?.media_type === 'video'
              ? getFullUrl(
                  item?.image?.[currentLocale],
                  process.env.BASE_URL_VIDEO
                )
              : null

          const gifUrl =
            item?.media_type === 'gif'
              ? getFullUrl(
                  item?.image?.[currentLocale],
                  process.env.BASE_URL_GIF
                )
              : null

          const bannerContent = (
            <AspectRatio
              display='grid'
              placeItems='center'
              position='relative'
              ratio={3 / 1}
              // maxH={{ base: '190px', lg: '727px' }}
              overflow='hidden'
              w={'full'}
              mx={'auto'}
            >
              {item?.media_type === 'video' && videoUrl ? (
                <video
                  style={{
                    width: '100%',
                    aspectRatio: `${aspectRatio}`,
                    objectFit: 'cover',
                  }}
                  autoPlay
                  muted
                  loop
                  preload='metadata'
                  playsInline
                >
                  <source src={videoUrl} />
                </video>
              ) : item?.media_type === 'gif' && gifUrl ? (
                <Image
                  src={gifUrl}
                  alt={`Banner ${item.title?.[currentLocale] || ''}`}
                  style={{
                    objectFit: 'cover',
                  }}
                  priority
                  fill
                />
              ) : imageUrl ? (
                <Image
                  src={imageUrl}
                  priority
                  style={{
                    objectFit: 'cover',
                  }}
                  loading='eager'
                  alt={`Banner ${item.title?.[currentLocale] || ''}`}
                  fill
                />
              ) : (
                <></>
              )}
            </AspectRatio>
          )

          const slideContent = (
            <div className='embla__slide' key={item.id || index}>
              <div
                style={{
                  position: 'relative',
                  cursor: item.url ? 'pointer' : 'default',
                }}
                onClick={() => {
                  if (item.url) {
                    window.open(
                      item.url,
                      isExternalUrl(item.url) ? '_blank' : '_self'
                    )
                  }
                }}
              >
                {bannerContent}
                {item?.title?.[currentLocale] &&
                  item?.title?.[currentLocale]?.length < 50 && (
                    <Center
                      position='absolute'
                      top={0}
                      left={0}
                      w='100%'
                      h='full'
                      bg='#00000073'
                    >
                      <Text
                        fontWeight={500}
                        fontSize={{ base: 24, lg: 56 }}
                        lineHeight={1.35}
                        letterSpacing='0.5%'
                        color='white'
                        textAlign='center'
                        maxW={{ base: '343px', lg: '1060px' }}
                        whiteSpace='pre-line'
                      >
                        {item?.title?.[currentLocale]}
                      </Text>
                    </Center>
                  )}
              </div>
            </div>
          )

          return slideContent
        }),
    [list, currentLocale]
  )

  if (!list?.length) return null

  return (
    <Box mt={isDesktop ? 0 : 4}>
      <Carousel slides={slides} />
    </Box>
  )
}

export default memo(Banner)
